/* eslint-disable i18next/no-literal-string */
import {Table} from '@dropbox/dig-components/table';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import cx from 'classnames';
import type {WireframeReportPillar, WireframeReportResponse} from 'client';

import {CategoryDonut, CategoryStackedBars, CategorySwatch, RankedBars} from './WireframeCharts';
import styles from './WireframeReport.module.css';
import {
  formatHeadcount,
  formatShare,
  formatUsd,
  shareOf,
  toNumber,
  WIREFRAME_CATEGORIES,
  WIREFRAME_GEOS,
} from './wireframeReportFormat';
import {WireframeSection} from './WireframeSection';

const PillarHeadcountTable = ({report}: {report: WireframeReportResponse}) => (
  <div className={styles.tableScroll}>
    <Table isNative spacing="small" hasDividers className={styles.numericTable}>
      <Table.Header>
        <Table.Row isHeader>
          <Table.HeaderCell>Pillar</Table.HeaderCell>
          {WIREFRAME_GEOS.map((geo) => (
            <Table.HeaderCell key={geo.key} className={styles.numeric}>
              {geo.shortLabel}
            </Table.HeaderCell>
          ))}
          <Table.HeaderCell className={styles.numeric}>HC</Table.HeaderCell>
          <Table.HeaderCell className={styles.numeric}>% of company</Table.HeaderCell>
          <Table.HeaderCell className={styles.numeric}>HC $</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {report.pillars.map((pillar) => (
          <Table.Row key={pillar.pillar_workstream_id}>
            <Table.Cell className={styles.nowrap}>
              <Text size="small">{pillar.name}</Text>
            </Table.Cell>
            {WIREFRAME_GEOS.map((geo) => (
              <Table.Cell key={geo.key} className={styles.numeric}>
                <Text size="small">{formatHeadcount(pillar.headcount[geo.headcount])}</Text>
              </Table.Cell>
            ))}
            <Table.Cell className={styles.numeric}>
              <Text size="small">{formatHeadcount(pillar.headcount.headcount_fte)}</Text>
            </Table.Cell>
            <Table.Cell className={styles.numeric}>
              <Text size="small">{formatShare(pillar.company_share)}</Text>
            </Table.Cell>
            <Table.Cell className={styles.numeric}>
              <Text size="small">{formatUsd(pillar.spend.total_usd)}</Text>
            </Table.Cell>
          </Table.Row>
        ))}
        <Table.Row className={styles.totalRow}>
          <Table.Cell className={styles.nowrap}>
            <Text size="small" isBold>
              Company total
            </Text>
          </Table.Cell>
          {WIREFRAME_GEOS.map((geo) => (
            <Table.Cell key={geo.key} className={styles.numeric}>
              <Text size="small" isBold>
                {formatHeadcount(report.headcount[geo.headcount])}
              </Text>
            </Table.Cell>
          ))}
          <Table.Cell className={styles.numeric}>
            <Text size="small" isBold>
              {formatHeadcount(report.headcount.headcount_fte)}
            </Text>
          </Table.Cell>
          <Table.Cell className={styles.numeric}>
            <Text size="small" isBold>
              {toNumber(report.headcount.headcount_fte) > 0 ? '100%' : formatShare(null)}
            </Text>
          </Table.Cell>
          <Table.Cell className={styles.numeric}>
            <Text size="small" isBold>
              {formatUsd(report.spend.total_usd)}
            </Text>
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  </div>
);

const TOP_WORKSTREAMS_LIMIT = 10;

const PillarCard = ({
  pillar,
  hrefForWorkstream,
}: {
  pillar: WireframeReportPillar;
  hrefForWorkstream: (id: number) => string;
}) => {
  const rowsById = new Map(pillar.rows.map((row) => [row.workstream_id, row]));
  const top = pillar.top_workstream_ids.slice(0, TOP_WORKSTREAMS_LIMIT).flatMap((id) => {
    const row = rowsById.get(id);
    return row ? [row] : [];
  });
  const total = toNumber(pillar.headcount.headcount_fte);
  return (
    <WireframeSection
      title={pillar.name}
      subtitle={`Top ${TOP_WORKSTREAMS_LIMIT} workstreams by headcount`}
      aside={
        <Text size="small" isBold className={styles.nowrap}>
          {formatHeadcount(total)} HC · {formatUsd(pillar.spend.total_usd)}
        </Text>
      }
    >
      <Stack gap="24">
        {top.length === 0 ? (
          <Text size="small" color="subtle">
            No workstream headcount entered yet.
          </Text>
        ) : (
          <RankedBars
            ariaLabel={`${pillar.name} top workstreams by headcount`}
            items={top.map((row) => ({
              key: row.workstream_id,
              label: row.name,
              href: hrefForWorkstream(row.workstream_id),
              value: toNumber(row.headcount.headcount_fte),
              detail: (
                <Text tagName="div" size="small" color="inherit">
                  {formatHeadcount(row.headcount.headcount_fte)} HC ({formatShare(row.pillar_share)}{' '}
                  of pillar) · {formatUsd(row.spend.total_usd)}
                </Text>
              ),
            }))}
          />
        )}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          paddingTop="16"
          columnGap="24"
          rowGap="24"
          className={styles.cardDivider}
        >
          <CategoryDonut headcount={pillar.headcount} spend={pillar.spend} title={pillar.name} />
          <dl className={cx(atoms({margin: '0'}), styles.categoryList)}>
            <Stack gap="12">
              {WIREFRAME_CATEGORIES.map((category) => {
                const value = toNumber(pillar.headcount[category.headcount]);
                return (
                  <div key={category.key}>
                    <dt className={atoms({display: 'flex', alignItems: 'center', columnGap: '10'})}>
                      <CategorySwatch category={category} />
                      <Text size="small" isBold>
                        {category.label}
                      </Text>
                      <Text size="small" color="subtle">
                        {formatShare(shareOf(value, total))}
                      </Text>
                    </dt>
                    <dd
                      className={atoms({
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '0',
                        // Under the label: past the 14px swatch and its 10px gap.
                        paddingLeft: '24',
                        paddingTop: '4',
                        columnGap: '16',
                      })}
                    >
                      <Text size="small" color="subtle">
                        {formatHeadcount(value)} HC
                      </Text>
                      <Text size="small">{formatUsd(pillar.spend[category.spend])}</Text>
                    </dd>
                  </div>
                );
              })}
            </Stack>
          </dl>
        </Box>
      </Stack>
    </WireframeSection>
  );
};

/** The whole half: headcount and spend by pillar, their category mix, and each pillar's largest workstreams. */
export const WireframeCompanyView = ({
  report,
  timeframeLabel,
  hrefForWorkstream,
}: {
  report: WireframeReportResponse;
  timeframeLabel: string;
  hrefForWorkstream: (id: number) => string;
}) => (
  <Stack gap="24">
    <WireframeSection
      title="Headcount by pillar"
      subtitle="By Cost Location"
      aside={
        <Text size="small" isBold className={styles.nowrap}>
          {timeframeLabel}
        </Text>
      }
    >
      <PillarHeadcountTable report={report} />
    </WireframeSection>
    <WireframeSection title="By cost category">
      <CategoryStackedBars
        rows={[
          ...report.pillars.map((pillar) => ({
            key: pillar.pillar_workstream_id,
            label: pillar.name,
            headcount: pillar.headcount,
            spend: pillar.spend,
          })),
          {
            key: 'company',
            label: 'Company total',
            headcount: report.headcount,
            spend: report.spend,
            emphasis: true,
          },
        ]}
      />
    </WireframeSection>
    <div className={styles.cardGrid}>
      {report.pillars.map((pillar) => (
        <PillarCard
          key={pillar.pillar_workstream_id}
          pillar={pillar}
          hrefForWorkstream={hrefForWorkstream}
        />
      ))}
    </div>
  </Stack>
);
